import { type TourArrowLayout } from './tourArrowGeometry';
import type { SpotlightRect } from './WorkspaceSpotlightTour';

interface CalloutLayout extends TourArrowLayout {}

interface CalloutCandidate extends CalloutLayout {
  placement: 'above' | 'below' | 'left' | 'right';
}

const CARD_WIDTH = 400;
const CARD_MARGIN = 16;
const CARD_GAP = 18;
const MIN_SIDE_CALLOUT_WIDTH = 280;
const MIN_CLEAR_SIDE_CALLOUT_WIDTH = 232;
const PREFERRED_CALLOUT_HEIGHT = 360;
const MAX_CALLOUT_HEIGHT = 620;
const MAX_MOBILE_CALLOUT_HEIGHT = 620;
const MIN_READABLE_CALLOUT_HEIGHT = 220;
const LARGE_TARGET_VIEWPORT_RATIO = 0.45;

export const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getHorizontalAnchorLeft = (
  spotlightRect: SpotlightRect,
  viewportWidth: number,
  calloutWidth: number
) => {
  const spotlightCenter = spotlightRect.left + spotlightRect.width / 2;
  const alignStart = spotlightCenter < viewportWidth / 2;
  const rawLeft = alignStart
    ? spotlightRect.left
    : spotlightRect.left + spotlightRect.width - calloutWidth;

  return clamp(rawLeft, CARD_MARGIN, viewportWidth - calloutWidth - CARD_MARGIN);
};

const getCandidatePriority = (placement: CalloutCandidate['placement']) => {
  switch (placement) {
    case 'below':
      return 0;
    case 'right':
      return 1;
    case 'left':
      return 2;
    case 'above':
    default:
      return 3;
  }
};

const getMaxCalloutHeight = (viewportWidth: number, viewportHeight: number) =>
  Math.min(
    viewportWidth < 600 ? MAX_MOBILE_CALLOUT_HEIGHT : MAX_CALLOUT_HEIGHT,
    viewportHeight - CARD_MARGIN * 2
  );

const pickBestCandidate = (candidates: CalloutCandidate[]) =>
  [...candidates].sort((leftCandidate, rightCandidate) => {
    if (rightCandidate.maxHeight !== leftCandidate.maxHeight) {
      return rightCandidate.maxHeight - leftCandidate.maxHeight;
    }

    if (rightCandidate.width !== leftCandidate.width) {
      return rightCandidate.width - leftCandidate.width;
    }

    return (
      getCandidatePriority(leftCandidate.placement) -
      getCandidatePriority(rightCandidate.placement)
    );
  })[0];

const getCalloutCandidateRect = (candidate: CalloutCandidate): SpotlightRect => ({
  top: candidate.top,
  left: candidate.left,
  width: candidate.width,
  height: candidate.maxHeight,
});

const doRectsOverlap = (firstRect: SpotlightRect, secondRect: SpotlightRect) =>
  firstRect.left < secondRect.left + secondRect.width &&
  firstRect.left + firstRect.width > secondRect.left &&
  firstRect.top < secondRect.top + secondRect.height &&
  firstRect.top + firstRect.height > secondRect.top;

const candidateAvoidsProtectedRects = (
  candidate: CalloutCandidate,
  protectedRects: readonly SpotlightRect[]
) =>
  protectedRects.every(
    (protectedRect) => !doRectsOverlap(getCalloutCandidateRect(candidate), protectedRect)
  );

const createProtectedRectVariants = (
  candidate: CalloutCandidate,
  protectedRects: readonly SpotlightRect[],
  viewportWidth: number,
  viewportHeight: number
): CalloutCandidate[] => {
  if (candidateAvoidsProtectedRects(candidate, protectedRects)) {
    return [candidate];
  }

  const maxTop = viewportHeight - candidate.maxHeight - CARD_MARGIN;
  const maxLeft = viewportWidth - candidate.width - CARD_MARGIN;
  const variants = protectedRects.flatMap((protectedRect) => {
    if (!doRectsOverlap(getCalloutCandidateRect(candidate), protectedRect)) {
      return [];
    }

    if (candidate.placement === 'left' || candidate.placement === 'right') {
      return [
        {
          ...candidate,
          top: clamp(protectedRect.top - CARD_GAP - candidate.maxHeight, CARD_MARGIN, maxTop),
        },
        {
          ...candidate,
          top: clamp(protectedRect.top + protectedRect.height + CARD_GAP, CARD_MARGIN, maxTop),
        },
      ];
    }

    return [
      {
        ...candidate,
        left: clamp(protectedRect.left - CARD_GAP - candidate.width, CARD_MARGIN, maxLeft),
      },
      {
        ...candidate,
        left: clamp(protectedRect.left + protectedRect.width + CARD_GAP, CARD_MARGIN, maxLeft),
      },
    ];
  });

  return [candidate, ...variants];
};

const protectCalloutCandidates = (
  candidates: readonly CalloutCandidate[],
  protectedRects: readonly SpotlightRect[],
  viewportWidth: number,
  viewportHeight: number
): CalloutCandidate[] => {
  if (protectedRects.length === 0) {
    return [...candidates];
  }

  const candidatesWithVariants = candidates.flatMap((candidate) =>
    createProtectedRectVariants(candidate, protectedRects, viewportWidth, viewportHeight)
  );
  const nonOverlappingCandidates = candidatesWithVariants.filter((candidate) =>
    candidateAvoidsProtectedRects(candidate, protectedRects)
  );

  return nonOverlappingCandidates.length > 0 ? nonOverlappingCandidates : candidatesWithVariants;
};

const createVerticalCandidate = (
  placement: 'above' | 'below',
  spotlightRect: SpotlightRect,
  viewportWidth: number,
  viewportHeight: number,
  availableHeight: number
): CalloutCandidate | null => {
  if (availableHeight <= 0) {
    return null;
  }

  const width = Math.min(CARD_WIDTH, viewportWidth - CARD_MARGIN * 2);
  const maxHeight = Math.min(getMaxCalloutHeight(viewportWidth, viewportHeight), availableHeight);
  const left = getHorizontalAnchorLeft(spotlightRect, viewportWidth, width);
  const spotlightBottom = spotlightRect.top + spotlightRect.height;
  const unclampedTop =
    placement === 'below'
      ? spotlightBottom + CARD_GAP
      : spotlightRect.top - CARD_GAP - maxHeight;

  return {
    placement,
    top: clamp(unclampedTop, CARD_MARGIN, viewportHeight - maxHeight - CARD_MARGIN),
    left,
    width,
    maxHeight,
  };
};

const createHorizontalCandidate = (
  placement: 'left' | 'right',
  spotlightRect: SpotlightRect,
  viewportWidth: number,
  viewportHeight: number,
  availableWidth: number,
  minimumWidth = MIN_SIDE_CALLOUT_WIDTH
): CalloutCandidate | null => {
  if (availableWidth < minimumWidth) {
    return null;
  }

  const width = Math.min(CARD_WIDTH, availableWidth);
  const maxHeight = getMaxCalloutHeight(viewportWidth, viewportHeight);
  const spotlightRight = spotlightRect.left + spotlightRect.width;
  const rawLeft =
    placement === 'right'
      ? spotlightRight + CARD_GAP
      : spotlightRect.left - CARD_GAP - width;

  return {
    placement,
    top: clamp(spotlightRect.top, CARD_MARGIN, viewportHeight - maxHeight - CARD_MARGIN),
    left: clamp(rawLeft, CARD_MARGIN, viewportWidth - width - CARD_MARGIN),
    width,
    maxHeight,
  };
};

const createCenteredLayout = (
  viewportWidth: number,
  viewportHeight: number
): CalloutLayout => {
  const width = Math.min(CARD_WIDTH, viewportWidth - CARD_MARGIN * 2);
  const maxHeight = getMaxCalloutHeight(viewportWidth, viewportHeight);

  return {
    top: clamp(
      Math.round((viewportHeight - maxHeight) / 2),
      CARD_MARGIN,
      viewportHeight - maxHeight - CARD_MARGIN
    ),
    left: clamp(
      Math.round((viewportWidth - width) / 2),
      CARD_MARGIN,
      viewportWidth - width - CARD_MARGIN
    ),
    width,
    maxHeight,
    placement: 'center',
  };
};

const createLargeTargetInsetLayout = (
  spotlightRect: SpotlightRect,
  viewportWidth: number,
  viewportHeight: number
): CalloutLayout => {
  const width = Math.min(CARD_WIDTH, viewportWidth - CARD_MARGIN * 2);
  const maxHeight = getMaxCalloutHeight(viewportWidth, viewportHeight);
  const inset = CARD_MARGIN;
  const prefersRightSide = spotlightRect.left < viewportWidth / 2;
  const rawLeft = prefersRightSide
    ? spotlightRect.left + spotlightRect.width - width - inset
    : spotlightRect.left + inset;
  const rawTop = spotlightRect.top + inset;

  return {
    top: clamp(rawTop, CARD_MARGIN, viewportHeight - maxHeight - CARD_MARGIN),
    left: clamp(rawLeft, CARD_MARGIN, viewportWidth - width - CARD_MARGIN),
    width,
    maxHeight,
    placement: 'center',
  };
};

const createClearBottomDockLayout = (
  spotlightRect: SpotlightRect,
  viewportWidth: number,
  viewportHeight: number,
  protectedRects: readonly SpotlightRect[]
): CalloutLayout => {
  const width = Math.min(CARD_WIDTH, viewportWidth - CARD_MARGIN * 2);
  const left = clamp(
    Math.round((viewportWidth - width) / 2),
    CARD_MARGIN,
    viewportWidth - width - CARD_MARGIN
  );
  const top = Math.max(CARD_MARGIN, spotlightRect.top + spotlightRect.height / 2);
  const protectedBottomBoundary = protectedRects.reduce(
    (boundary, protectedRect) =>
      protectedRect.top >= top &&
      protectedRect.left < left + width &&
      protectedRect.left + protectedRect.width > left
        ? Math.min(boundary, protectedRect.top)
        : boundary,
    viewportHeight - CARD_MARGIN
  );
  const maxHeight = Math.min(
    getMaxCalloutHeight(viewportWidth, viewportHeight),
    Math.floor(viewportHeight * LARGE_TARGET_VIEWPORT_RATIO),
    Math.max(0, protectedBottomBoundary - top)
  );

  return {
    top,
    left,
    width,
    maxHeight,
    placement: 'below',
  };
};

export const computeCalloutLayout = (
  spotlightRect: SpotlightRect,
  viewportWidth: number,
  viewportHeight: number,
  protectedRects: readonly SpotlightRect[] = [],
  keepSpotlightClear = false
): CalloutLayout => {
  const calloutWidth = Math.min(CARD_WIDTH, viewportWidth - CARD_MARGIN * 2);
  const spotlightBottom = spotlightRect.top + spotlightRect.height;
  const spotlightRight = spotlightRect.left + spotlightRect.width;
  const availableAbove = Math.max(0, spotlightRect.top - CARD_GAP - CARD_MARGIN);
  const availableBelow = Math.max(0, viewportHeight - spotlightBottom - CARD_GAP - CARD_MARGIN);
  const availableLeft = Math.max(0, spotlightRect.left - CARD_GAP - CARD_MARGIN);
  const availableRight = Math.max(0, viewportWidth - spotlightRight - CARD_GAP - CARD_MARGIN);
  const spotlightAreaRatio =
    (spotlightRect.width * spotlightRect.height) / Math.max(1, viewportWidth * viewportHeight);

  const belowCandidate = createVerticalCandidate(
    'below',
    spotlightRect,
    viewportWidth,
    viewportHeight,
    availableBelow
  );
  const aboveCandidate = createVerticalCandidate(
    'above',
    spotlightRect,
    viewportWidth,
    viewportHeight,
    availableAbove
  );
  const rightCandidate = createHorizontalCandidate(
    'right',
    spotlightRect,
    viewportWidth,
    viewportHeight,
    availableRight,
    keepSpotlightClear ? MIN_CLEAR_SIDE_CALLOUT_WIDTH : MIN_SIDE_CALLOUT_WIDTH
  );
  const leftCandidate = createHorizontalCandidate(
    'left',
    spotlightRect,
    viewportWidth,
    viewportHeight,
    availableLeft,
    keepSpotlightClear ? MIN_CLEAR_SIDE_CALLOUT_WIDTH : MIN_SIDE_CALLOUT_WIDTH
  );

  if (keepSpotlightClear) {
    const clearCandidates = protectCalloutCandidates(
      [rightCandidate, leftCandidate, belowCandidate, aboveCandidate].filter(
        (candidate): candidate is CalloutCandidate => Boolean(candidate)
      ),
      protectedRects,
      viewportWidth,
      viewportHeight
    ).filter(
      (candidate) =>
        candidate.maxHeight >= MIN_READABLE_CALLOUT_HEIGHT &&
        candidateAvoidsProtectedRects(candidate, [spotlightRect, ...protectedRects])
    );

    if (clearCandidates.length > 0) {
      return pickBestCandidate(clearCandidates);
    }

    return createClearBottomDockLayout(
      spotlightRect,
      viewportWidth,
      viewportHeight,
      protectedRects
    );
  }

  const verticalCandidates = [belowCandidate, aboveCandidate].filter(
    (candidate): candidate is CalloutCandidate => Boolean(candidate)
  );
  const protectedVerticalCandidates = protectCalloutCandidates(
    verticalCandidates,
    protectedRects,
    viewportWidth,
    viewportHeight
  );
  const spaciousVerticalCandidates = protectedVerticalCandidates.filter(
    (candidate) => candidate.maxHeight >= PREFERRED_CALLOUT_HEIGHT
  );

  if (spaciousVerticalCandidates.length > 0) {
    const bestVerticalCandidate = pickBestCandidate(spaciousVerticalCandidates);
    return {
      top: bestVerticalCandidate.top,
      left: bestVerticalCandidate.left,
      width: bestVerticalCandidate.width,
      maxHeight: bestVerticalCandidate.maxHeight,
      placement: bestVerticalCandidate.placement,
    };
  }

  const allCandidates = [
    rightCandidate,
    leftCandidate,
    belowCandidate,
    aboveCandidate,
  ].filter((candidate): candidate is CalloutCandidate => Boolean(candidate));
  const protectedCandidates = protectCalloutCandidates(
    allCandidates,
    protectedRects,
    viewportWidth,
    viewportHeight
  );

  if (protectedCandidates.length > 0) {
    const bestCandidate = pickBestCandidate(protectedCandidates);
    if (
      bestCandidate.maxHeight < PREFERRED_CALLOUT_HEIGHT &&
      spotlightAreaRatio >= LARGE_TARGET_VIEWPORT_RATIO
    ) {
      return createLargeTargetInsetLayout(spotlightRect, viewportWidth, viewportHeight);
    }

    if (bestCandidate.maxHeight < MIN_READABLE_CALLOUT_HEIGHT) {
      return createCenteredLayout(viewportWidth, viewportHeight);
    }

    return {
      top: bestCandidate.top,
      left: bestCandidate.left,
      width: bestCandidate.width,
      maxHeight: bestCandidate.maxHeight,
      placement: bestCandidate.placement,
    };
  }

  const centeredLayout = createCenteredLayout(viewportWidth, viewportHeight);
  if (spotlightAreaRatio >= LARGE_TARGET_VIEWPORT_RATIO) {
    return createLargeTargetInsetLayout(spotlightRect, viewportWidth, viewportHeight);
  }

  return {
    top: centeredLayout.top,
    left: centeredLayout.left,
    width: calloutWidth,
    maxHeight: centeredLayout.maxHeight,
    placement: 'center',
  };
};
